<template>
  <div class="bg-white rounded-xl border border-gray-200 p-4">
    <!-- Active filter chips -->
    <div v-if="hasActiveFilters" class="flex flex-wrap gap-2 mb-3 pb-3 border-b border-gray-100">
      <span class="text-xs text-gray-500 self-center">Filters:</span>
      <Tag
        v-for="chip in activeFilterChips"
        :key="chip.key"
        :label="chip.label"
        color="primary"
        :removable="true"
        @remove="clearFilter(chip.key)"
      />
      <button @click="$emit('clear-all')" class="text-xs text-gray-500 hover:text-primary-600 transition-colors ml-2">Clear all</button>
    </div>

    <!-- Primary filters -->
    <div class="flex flex-wrap gap-3 items-end mb-3">
      <FilterSelect :modelValue="filter.state" @update:modelValue="updateFilter('state', $event)" label="State" :options="stateOptions" placeholder="All" />
      <FilterInput :modelValue="filter.minBenes" @update:modelValue="updateFilter('minBenes', $event)" @keyup.enter="$emit('search')" label="Min Benes" type="number" placeholder="0" class="w-20" />
      <FilterInput :modelValue="filter.maxBenes" @update:modelValue="updateFilter('maxBenes', $event)" @keyup.enter="$emit('search')" label="Max Benes" type="number" placeholder="Any" class="w-20" />
      <div class="flex-1">
        <label class="block text-xs font-medium mb-1 text-gray-500">Search</label>
        <div class="relative">
          <svg class="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
          <input :value="filter.search" @input="updateFilter('search', $event.target.value)" @keyup.enter="$emit('search')" type="text" placeholder="Practice name or ID..." class="w-full border border-gray-200 rounded-lg px-2 py-1.5 pl-8 text-sm focus:ring-2 focus:ring-primary-300 focus:border-primary-300" />
        </div>
      </div>
      <button @click="$emit('search')" class="px-4 py-1.5 rounded-lg text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 transition-colors">Search</button>
    </div>

    <!-- Tag filters (config-driven) -->
    <TagFilterPanel
      :dimensions="tagDimensions"
      :groups="tagGroups"
      :filter="filter"
      @update:filter="$emit('update:filter', $event)"
      @search="$emit('search')"
    />
  </div>
</template>

<script>
import FilterSelect from '../FilterSelect.vue'
import FilterInput from '../FilterInput.vue'
import TagFilterPanel from './TagFilterPanel.vue'
import Tag from '@/components/widgetcomponents/library/Tag.vue'

const STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY','DC','PR'
]

export default {
  name: 'PracticeFilter',
  components: { FilterSelect, FilterInput, TagFilterPanel, Tag },
  props: {
    filter: { type: Object, required: true },
    tagDimensions: { type: Array, default: () => [] },
    tagGroups: { type: Array, default: () => [] },
  },
  emits: ['update:filter', 'search', 'clear-filter', 'clear-all'],
  computed: {
    stateOptions() { return STATES.map(st => ({ value: st, label: st })) },
    hasActiveFilters() {
      const f = this.filter
      if (f.state || f.search || f.minBenes || f.maxBenes) return true
      return this.tagDimensions.some(d => f[d.key])
    },
    activeFilterChips() {
      const chips = []
      const f = this.filter
      // Structural filters
      if (f.state) chips.push({ key: 'state', label: `State: ${f.state}` })
      if (f.search) chips.push({ key: 'search', label: `Search: ${f.search}` })
      if (f.minBenes) chips.push({ key: 'minBenes', label: `Min: ${f.minBenes}` })
      if (f.maxBenes) chips.push({ key: 'maxBenes', label: `Max: ${f.maxBenes}` })
      // Dynamic tag filters (supports comma-separated multi-select)
      for (const dim of this.tagDimensions) {
        if (f[dim.key]) {
          const values = f[dim.key].split(',')
          const labels = values.map(v => {
            const opt = this.findOption(dim, v.trim())
            return opt?.label || v.trim()
          })
          chips.push({ key: dim.key, label: `${dim.label}: ${labels.join(', ')}` })
        }
      }
      return chips
    },
  },
  methods: {
    updateFilter(key, value) {
      this.$emit('update:filter', { ...this.filter, [key]: value })
      if (key !== 'search' && key !== 'minBenes' && key !== 'maxBenes') {
        this.$emit('search')
      }
    },
    clearFilter(key) {
      this.$emit('clear-filter', key)
    },
    findOption(dim, value) {
      if (dim.options) return dim.options.find(o => o.value === value)
      if (dim.option_groups) {
        for (const g of dim.option_groups) {
          const found = g.options.find(o => o.value === value)
          if (found) return found
        }
      }
      return null
    },
  },
}
</script>
